import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useSpring,
  useReducedMotion,
} from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';

/* ── Section → tint colour mapping (very subtle) ── */
const SECTION_TINTS = {
  hero:      { bg: 'rgba(255, 200, 120, 0.08)', shadow: 'rgba(200, 140, 60, 0.06)' },
  features:  { bg: 'rgba(140, 190, 140, 0.08)', shadow: 'rgba(70, 120, 70, 0.06)' },
  commerce:  { bg: 'rgba(220, 140, 140, 0.08)', shadow: 'rgba(180, 80, 80, 0.06)' },
  workflow:  { bg: 'rgba(160, 140, 200, 0.08)', shadow: 'rgba(100, 80, 160, 0.06)' },
  support:   { bg: 'rgba(100, 160, 140, 0.08)', shadow: 'rgba(40, 100, 80, 0.06)' },
};

const DEFAULT_TINT = SECTION_TINTS.hero;

/* ── Nav link definitions ── */
const PRODUCT_LINKS = [
  { id: 'features', label: 'Platform' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'vault', label: 'Security' },
  { id: 'support', label: 'Support' },
];

/* ── Component ── */
const LivingGlassNav = ({
  scrollYProgress,
  activeSection,
  onScrollToSection,
  mobileMenuOpen,
  onToggleMobile,
}) => {
  const reduceMotion = useReducedMotion();
  const navRef = useRef(null);

  /* ── Scroll-derived values ── */
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const navMaxWidth = useTransform(smoothProgress, [0, 0.08], [1100, 860]);
  const navPadX     = useTransform(smoothProgress, [0, 0.08], [24, 18]);
  const navPadY     = useTransform(smoothProgress, [0, 0.08], [12, 8]);

  const progressDeg = useTransform(smoothProgress, [0, 1], [0, 360]);
  const progressOffset = useTransform(progressDeg, (deg) => {
    const circumference = 2 * Math.PI * 18;
    return circumference - (deg / 360) * circumference;
  });

  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const unsubscribe = smoothProgress.on('change', (v) => setIsScrolled(v > 0.04));
    return unsubscribe;
  }, [smoothProgress]);

  const currentTint = SECTION_TINTS[activeSection] || DEFAULT_TINT;

  /* ── Cursor lens effect ── */
  const cursorX = useMotionValue(0.5);
  const cursorY = useMotionValue(0.5);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (reduceMotion || !navRef.current) return;
    const rect = navRef.current.getBoundingClientRect();
    cursorX.set((e.clientX - rect.left) / rect.width);
    cursorY.set((e.clientY - rect.top) / rect.height);
  }, [reduceMotion, cursorX, cursorY]);

  const handleMouseEnter = useCallback(() => setIsHovering(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    cursorX.set(0.5);
    cursorY.set(0.5);
  }, [cursorX, cursorY]);

  /* ── Dynamic Expanding Cluster State ── */
  const [isClusterExpanded, setIsClusterExpanded] = useState(false);
  
  // Is the current active section part of the product cluster?
  const isProductActive = PRODUCT_LINKS.some(link => link.id === activeSection);
  // Get the active product label to show when collapsed
  const activeProductLabel = PRODUCT_LINKS.find(link => link.id === activeSection)?.label || 'Product';

  const navStyle = useMemo(() => ({
    maxWidth: navMaxWidth,
    paddingLeft: navPadX,
    paddingRight: navPadX,
    paddingTop: navPadY,
    paddingBottom: navPadY,
    '--nav-tint-bg': currentTint.bg,
    '--nav-tint-shadow': currentTint.shadow,
    '--cursor-x': cursorX,
    '--cursor-y': cursorY,
  }), [navMaxWidth, navPadX, navPadY, currentTint, cursorX, cursorY]);

  return (
    <>
      <motion.nav
        ref={navRef}
        className={cn(
          'living-nav',
          isScrolled && 'living-nav-scrolled',
          isHovering && 'living-nav-hover',
        )}
        style={navStyle}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        initial={reduceMotion ? { x: "-50%" } : { x: "-50%", y: -40, opacity: 0 }}
        animate={{ x: "-50%", y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      >
        {/* ── Logo ── */}
        <Link to="/" className="living-nav-brand" aria-label="Linkit">
          <div className="living-nav-logo-ring">
            <svg viewBox="0 0 40 40" className="living-nav-progress-svg" aria-hidden="true">
              <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.08" />
              <motion.circle
                cx="20" cy="20" r="18"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 18}`}
                style={{ strokeDashoffset: progressOffset }}
                className="living-nav-progress-stroke"
              />
            </svg>
            <span className="living-nav-brand-mark">L</span>
          </div>
          <span className="landing-wordmark living-nav-wordmark">linkit</span>
        </Link>

        {/* ── Dynamic Nav Links ── */}
        <div className="living-nav-links" onMouseLeave={() => setIsClusterExpanded(false)}>
          
          {/* Expanding Product Cluster */}
          <motion.div 
            className="living-nav-cluster"
            layout
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            onMouseEnter={() => setIsClusterExpanded(true)}
          >
            <AnimatePresence mode="popLayout">
              {!isClusterExpanded ? (
                <motion.button
                  key="collapsed"
                  className={cn("living-nav-link living-nav-cluster-trigger", isProductActive && "living-nav-link-active")}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setIsClusterExpanded(true)}
                >
                  {isProductActive ? activeProductLabel : 'Product'}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 opacity-50"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </motion.button>
              ) : (
                <motion.div
                  key="expanded"
                  className="living-nav-cluster-expanded"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.25 }}
                >
                  {PRODUCT_LINKS.map(link => (
                    <button
                      key={link.id}
                      onClick={() => {
                        onScrollToSection(link.id);
                        setIsClusterExpanded(false);
                      }}
                      className={cn("living-nav-link", activeSection === link.id && "living-nav-link-active")}
                    >
                      {link.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Standalone Links */}
          {/* <Link to="/templates" className="living-nav-link">
            Templates
          </Link> */}
          <Link to="/discover" className="living-nav-link">
            Discover
          </Link>
          <Link to="/pricing" className="living-nav-link">
            Pricing
          </Link>
        </div>

        {/* ── CTA Buttons ── */}
        <div className="living-nav-cta">
          <Link to="/login" className="living-nav-btn-ghost">
            Sign In
          </Link>
          <Link to="/login?signup=true" className="living-nav-btn-primary group">
            <span>Get Started</span>
            <ArrowRight size={14} className="living-nav-btn-arrow" />
          </Link>
        </div>

        {/* ── Mobile hamburger ── */}
        <button
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={onToggleMobile}
          className="living-nav-hamburger"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* ── Cursor lens highlight ── */}
        {isHovering && !reduceMotion && (
          <motion.div
            className="living-nav-lens"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </motion.nav>

      {/* ── Mobile dropdown ── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="living-nav-mobile-overlay"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="living-nav-mobile-menu">
              <Link to="/login" onClick={onToggleMobile} className="living-nav-mobile-btn">
                Sign In
              </Link>
              <Link to="/login?signup=true" onClick={onToggleMobile} className="living-nav-mobile-btn living-nav-mobile-btn-primary">
                Get Started
              </Link>
              <div className="living-nav-mobile-footer">
                <button type="button" onClick={() => { onScrollToSection('features'); onToggleMobile(); }}>Platform</button>
                <button type="button" onClick={() => { onScrollToSection('commerce'); onToggleMobile(); }}>Commerce</button>
                <button type="button" onClick={() => { onScrollToSection('workflow'); onToggleMobile(); }}>Workflow</button>
                <Link to="/blueprint" onClick={onToggleMobile}>Security</Link>
                <Link to="/discover" onClick={onToggleMobile}>Discover</Link>
                <Link to="/pricing" onClick={onToggleMobile}>Pricing</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LivingGlassNav;
